﻿import {
    AccessRequestOutcome,
    AccessRequestType,
    IncidentLogSaveModel,
    InformationSecurityProperty,
    ReceiptChannel,
    SeverityLevelEnum,
    YesNoAnswer,
    YesNoNotNecessaryAnswer,
    YesNoUnknownAnswer,
} from "../IncidentLogDialog.types";
import { useEffect, useState } from "react";
import { useSelectableDataCategoriesQuery } from "../../processingActivity/hooks/useSelectableDataCategoriesQuery";
import { isNullOrWhitespace, setFirstLetterToLowerCase } from "../../common/stringOperations";
import { useTranslation } from "../../localization/useTranslation";
import { post, put, uploadFile } from "@common/api/apiShared";
import { useUploadProgress } from "@common/components/uploadProgress/useUploadProgress";
import { getFileToUpload } from "@common/documentUploadFile";
import { ICreateEditIncidentDialogProps } from "./CreateEditIncidentLogDialog";
import { DocumentUploadViewModel, DotLegalFile, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { QuestionTypeStandardAnswers } from "../../riskAssessment/RiskAssessment.types";
import { getQuestionCategoryLists } from "../../riskAssessment/riskAssessmentTemplate/RiskAssessmentTemplate.types";
import { DotLegalSelectOption } from "../../common/components/dotLegalMultiSelect/DotLegalMultiSelect.types";
import { SelectableDataCategoryItem, SelectableItem } from "../../processingActivity/ProcessingActivity.types";
import { useSecurityMeasuresQuery, useSelectableIncidentTypesQuery } from "@common/hooks/useSelectableItemQueries.ts";
import { useValidator } from "../../common/hooks/validation/index.ts";
import { mapToSaveModel as mapIncidentLogDocumentToSaveModel } from "../incidentLogOverview/documents/incidentDocumentPane/IncidentDocumentPane.hooks.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useCreateEditIncidentLogDialogDataMapping(props: ICreateEditIncidentDialogProps) {
    let dataCategoriesQuery = useSelectableDataCategoriesQuery();
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    const [documentUploadModel, setDocumentUploadModel] = useState<DocumentUploadViewModel>(new DocumentUploadViewModel([], undefined));
    const [showAddDocument, setShowAddDocument] = useState(false);
    const [saveModel, setSaveModel] = useState<IncidentLogSaveModel>({
        name: "",
        entityTemplateId: null,
        dateOfIncident: null,
        unknownDateOfIncident: false,
        cause: null,
        consequences: null,
        measures: null,
        reportedToAgency: null,
        dateOfReportedToAgency: null,
        reasonForNotReportingToAgency: null,
        affectedPeopleNotified: null,
        reasonForNotNotifyingAffectedPeople: null,
        dateOfAffectedPeopleNotified: null,
        dataCategories: [],
        types: [],
        timeOfIncident: null,
        severity: SeverityLevelEnum.Moderate,
        tags: [],
        relations: [],
        document: { name: "", fileName: "", link: "" },
    });

    function isEdit() {
        return props.id !== undefined;
    }

    let dataCategoriesData: Array<DotLegalSelectOption> | undefined;
    const { isLoading, data } = useDotLegalQuery<IncidentLogSaveModel>(`/incidentLogEntry/${props.id}`, {
        enabled: isEdit(),
    });

    const validation = useValidator<IncidentLogSaveModel>(
        (validators) => ({
            name: validators.validateNotEmpty((item) => item.name, translateString("name")),
            document: showAddDocument ? validators.validateNotEmpty((item) => item.document?.name, translateString("name")) : undefined,
        }),
        [showAddDocument]
    );

    validation.check(saveModel);

    const { data: incidentTypes } = useSelectableIncidentTypesQuery();

    useEffect(() => {
        if (data) {
            setSaveModel({ ...data });
        }
    }, [data]);

    if (dataCategoriesQuery.allDataCategories) {
        dataCategoriesData = getDataCategorySelectOptions(dataCategoriesQuery.allDataCategories);
    }

    let securityMeasuresQuery = useSecurityMeasuresQuery(true, false);
    const securityMeasures = getSecurityMeasureSuggestions(securityMeasuresQuery.data);

    const { uploadPercentage, trackUploadProgress } = useUploadProgress();
    const saveMutation = useDotLegalMutation(submitSave);

    async function submitSave(saveModel: IncidentLogSaveModel) {
        const file = showAddDocument ? getFileToUpload(documentUploadModel.files) : undefined;
        const uploadId = file ? await uploadFile(file, trackUploadProgress()) : undefined;
        const document = saveModel.document && { ...saveModel.document, uploadId };
        return post("/incidentLogEntry/", { ...saveModel, document });
    }

    const save = () => {
        var temp = { ...saveModel };
        if (showAddDocument) {
            temp.document = mapIncidentLogDocumentToSaveModel(saveModel.document?.name, documentUploadModel);
        } else {
            temp.document = undefined;
        }

        saveMutation.mutateAsync(temp, {
            onSuccess: () => {
                snackbar.show(translateString("incidentCreatedSnackbar"));
                props.onSave();
            },
        });
    };

    const editMutation = useDotLegalMutation(submitEdit);

    function submitEdit(saveModel: IncidentLogSaveModel) {
        return put(`/incidentLogEntry/${props.id}`, saveModel);
    }

    const edit = () => {
        editMutation.mutateAsync(saveModel, {
            onSuccess: () => {
                snackbar.show(translateString("incidentUpdatedSnackbar"));
                props.onSave();
            },
        });
    };

    const consequenceSuggestions = getConsequenceSuggestions(translateString);

    function validateAndSave() {
        validation.setShowErrors(true);

        if (validation.anyHasErrors || (showAddDocument && !documentUploadModel.IsModelValid())) {
            return;
        }

        if (isEdit()) {
            edit();
        } else {
            save();
        }
    }

    async function setDocumentName(files: DotLegalFile[] | undefined) {
        let temp = { ...saveModel! };

        let fileName = files && files!.length > 0 ? files![0].fileName : "";

        if (isNullOrWhitespace(temp.document?.name)) {
            temp.document!.name = fileName.substring(0, fileName.lastIndexOf("."));
        }

        setSaveModel(temp);
    }

    return {
        saveModel,
        setSaveModel,
        dataCategoriesData,
        validateAndSave,
        consequenceSuggestions,
        securityMeasures,
        isLoading,
        isEdit,
        incidentTypes,
        isSaving: editMutation.isLoading || saveMutation.isLoading,
        isUploadingDocument: saveMutation.isLoading && showAddDocument,
        uploadPercentage,
        severityLevelSelectOptions: getSeverityLevelSelectOptions(translateString),
        documentUploadModel,
        setDocumentUploadModel,
        validation,
        showAddDocument,
        setShowAddDocument,
        setDocumentName,
    };
}

export function getConsequenceSuggestions(translateString: (key: string) => string): Array<string> {
    return getQuestionCategoryLists()
        .flatMap((x) => x.questionTypes)
        .map((x) => new QuestionTypeStandardAnswers(x, translateString).consequenceAnswers)
        .flatMap((x) => x)
        .filter((value, index, array) => array.indexOf(value) === index) //multiple QuestionTypes have some of the same consequences
        .sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
}

export function getSecurityMeasureSuggestions(securityMeasures: Array<SelectableItem> | undefined): Array<string> {
    return (securityMeasures ?? []).map((x) => x.name).sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1));
}

export function getDataCategorySelectOptions(dataCategories: Array<SelectableDataCategoryItem> | undefined): Array<DotLegalSelectOption> {
    return (dataCategories ?? []).map((dataCategory) => ({ id: dataCategory.id, name: dataCategory.name, hoverText: dataCategory.description }));
}

export function getSeverityLevelSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return [
        { id: SeverityLevelEnum.VeryLow.toString(), name: translateString("veryLow") },
        { id: SeverityLevelEnum.Low.toString(), name: translateString("low") },
        { id: SeverityLevelEnum.Moderate.toString(), name: translateString("moderate") },
        { id: SeverityLevelEnum.High.toString(), name: translateString("high") },
        { id: SeverityLevelEnum.VeryHigh.toString(), name: translateString("veryHigh") },
    ];
}

export function getAccessRequestTypeSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return Object.values(AccessRequestType)
        .filter((value): value is AccessRequestType => typeof value === "number")
        .map((accessRequestType) => ({
            id: accessRequestType.toString(),
            name: translateString(setFirstLetterToLowerCase(AccessRequestType[accessRequestType])),
        }));
}

export function getAccessRequestOutcomeSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return Object.values(AccessRequestOutcome)
        .filter((value): value is AccessRequestOutcome => typeof value === "number")
        .map((accessRequestOutcome) => ({
            id: accessRequestOutcome.toString(),
            name: translateString(setFirstLetterToLowerCase(AccessRequestOutcome[accessRequestOutcome])),
        }));
}

export function getReceiptChannelSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return Object.values(ReceiptChannel)
        .filter((value): value is ReceiptChannel => typeof value === "number")
        .map((receiptChannel) => ({
            id: receiptChannel.toString(),
            name: translateString(setFirstLetterToLowerCase(ReceiptChannel[receiptChannel])),
        }));
}

export function getYesNoAnswerSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return [
        { id: YesNoAnswer.Yes.toString(), name: translateString("yes") },
        { id: YesNoAnswer.No.toString(), name: translateString("no") },
    ];
}

export function getYesNoNotNecessaryAnswerSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return [
        { id: YesNoNotNecessaryAnswer.Yes.toString(), name: translateString("yes") },
        { id: YesNoNotNecessaryAnswer.No.toString(), name: translateString("no") },
        { id: YesNoNotNecessaryAnswer.NotNecessary.toString(), name: translateString("notNecessary") },
    ];
}

export function getYesNoUnknownAnswerSelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return [
        { id: YesNoUnknownAnswer.Yes.toString(), name: translateString("yes") },
        { id: YesNoUnknownAnswer.No.toString(), name: translateString("no") },
        { id: YesNoUnknownAnswer.Unknown.toString(), name: translateString("unknown") },
    ];
}

export function getInformationSecurityPropertySelectOptions(translateString: (key: string) => string): Array<DotLegalSelectOption> {
    return Object.values(InformationSecurityProperty)
        .filter((value): value is InformationSecurityProperty => typeof value === "number")
        .map((property) => ({
            id: property.toString(),
            name: translateString(setFirstLetterToLowerCase(InformationSecurityProperty[property])),
        }));
}
